import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, MenuItem, Snackbar, TextField } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CleaningServicesRoundedIcon from "@mui/icons-material/CleaningServicesRounded";
import { useSnackbar } from "../../hooks/useSnackbar";
import { Panel, FilterGrid, ListGrid, GhostButton, Badge } from "./ui";
import { formatKurus, LOW_BALANCE_KURUS } from "./constants";
import { getPersonal } from "../../api/digitalPayAPI";
import { isSuperAdminUser } from "./roleUtils";
import {
  createAndDownloadExcel,
  generateExcelFilename,
} from "../../utils/excelExportUtils";

const STATUS = { ALL: "", LOW: "low", OK: "ok" };

export default function CardsPanel({ lookups }) {
  const { firms = [], departments = [] } = lookups;
  const canSelectFirm = isSuperAdminUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const effectiveFirms = useMemo(() => {
    const firmsById = new Map(
      firms.map((firm) => [Number(firm.idfirm), firm])
    );
    rows.forEach((row) => {
      const idfirm = Number(row.firm_idfirm);
      if (idfirm && row.firmName && !firmsById.has(idfirm)) {
        firmsById.set(idfirm, { idfirm, name: row.firmName });
      }
    });
    return Array.from(firmsById.values());
  }, [firms, rows]);
  const assignedFirmId = canSelectFirm
    ? ""
    : (effectiveFirms[0]?.idfirm ?? "");
  const [filter, setFilter] = useState({
    search: "",
    firm: assignedFirmId,
    dept: "",
    status: STATUS.ALL,
  });
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await getPersonal());
    } catch (e) {
      showSnackbar(e.message || "Kartlar alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canSelectFirm) {
      setFilter((current) => ({ ...current, firm: assignedFirmId }));
    }
  }, [assignedFirmId, canSelectFirm]);

  const departmentById = useMemo(
    () => new Map(departments.map((d) => [Number(d.iddepartment), d])),
    [departments]
  );
  const firmById = useMemo(
    () => new Map(effectiveFirms.map((f) => [Number(f.idfirm), f])),
    [effectiveFirms]
  );

  // Departman seçenekleri seçili firmaya göre daralır.
  const deptOptions = useMemo(
    () =>
      departments.filter((d) => !filter.firm || d.firm_idfirm === Number(filter.firm)),
    [departments, filter.firm]
  );

  const filtered = useMemo(() => {
    const q = filter.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter.firm && Number(r.firm_idfirm) !== Number(filter.firm)) return false;
      if (filter.dept && Number(r.department_iddepartment) !== Number(filter.dept)) return false;
      if (filter.status === STATUS.LOW && r.cardBalance >= LOW_BALANCE_KURUS) return false;
      if (filter.status === STATUS.OK && r.cardBalance < LOW_BALANCE_KURUS) return false;
      if (q) {
        const hay = `${r.name} ${r.surname} ${r.regNumber} ${r.cardNo}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filter]);

  const columns = [
    { field: "regNumber", headerName: "Sicil", flex: 1, minWidth: 110 },
    {
      field: "nameSurname",
      headerName: "Ad Soyad",
      flex: 1,
      minWidth: 150,
      valueGetter: (_v, row) => `${row.name ?? ""} ${row.surname ?? ""}`.trim(),
      sortComparator: (a, b) =>
        String(a ?? "").localeCompare(String(b ?? ""), "tr", {
          sensitivity: "base",
        }),
    },
    { field: "cardNo", headerName: "Kart No", flex: 1, minWidth: 130 },
    {
      field: "firm_idfirm",
      headerName: "Firma / Departman",
      flex: 1.3,
      minWidth: 180,
      valueGetter: (value, row) => {
        const f = firmById.get(Number(value))?.name ?? row.firmName ?? "";
        const d = departmentById.get(Number(row.department_iddepartment))?.name ?? "";
        return [f, d].filter(Boolean).join(" / ");
      },
    },
    {
      field: "cardBalance",
      headerName: "Standart B.",
      width: 120,
      cellClassName: "dp-balance-cell",
      valueFormatter: (v) => formatKurus(v),
    },
    {
      field: "cardIsgBalance",
      headerName: "İSG B.",
      width: 110,
      cellClassName: "dp-balance-cell",
      valueFormatter: (v) => formatKurus(v),
    },
    {
      field: "__status",
      headerName: "Durum",
      width: 130,
      sortable: false,
      renderCell: (params) =>
        params.row.cardBalance < LOW_BALANCE_KURUS ? (
          <Badge tone="danger">Düşük Bakiye</Badge>
        ) : (
          <Badge tone="success">Yeterli</Badge>
        ),
    },
  ];

  const handleExport = () => {
    const exportColumns = columns.filter((column) => column.field !== "__status");
    const result = createAndDownloadExcel(
      filtered,
      exportColumns,
      "Kartlar",
      generateExcelFilename("Vendai_Digital_Pay_Kartlar"),
      {
        valueTransformer: (value, column, row) => {
          if (column.field === "nameSurname") {
            return `${row.name ?? ""} ${row.surname ?? ""}`.trim();
          }
          if (column.field === "firm_idfirm") {
            const firmName =
              firmById.get(Number(row.firm_idfirm))?.name ?? row.firmName ?? "";
            const departmentName =
              departmentById.get(Number(row.department_iddepartment))?.name ?? "";
            return [firmName, departmentName].filter(Boolean).join(" / ");
          }
          if (column.field === "cardBalance" || column.field === "cardIsgBalance") {
            return formatKurus(value);
          }
          return value;
        },
      }
    );

    if (result.success) {
      showSnackbar(`İndirildi: ${result.fileName}`, "success");
    } else {
      showSnackbar(result.error || "Dışa aktarılamadı", "error");
    }
  };

  return (
    <>
      <Panel
        title="Kart İzleme"
        sub={`${filtered.length} kart listeleniyor · ${formatKurus(
          LOW_BALANCE_KURUS
        )} altındaki bakiyeler düşük sayılır`}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <GhostButton
              startIcon={<CleaningServicesRoundedIcon />}
              onClick={() =>
                setFilter({
                  search: "",
                  firm: assignedFirmId,
                  dept: "",
                  status: STATUS.ALL,
                })
              }
            >
              Temizle
            </GhostButton>
            <GhostButton startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Yenile
            </GhostButton>
            <GhostButton startIcon={<FileDownloadIcon />} onClick={handleExport}>
              Dışa Aktar
            </GhostButton>
          </Box>
        }
      >
        <FilterGrid>
          <TextField
            label="Ara (ad, sicil, kart)"
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            select
            label="Firma"
            value={filter.firm}
            onChange={(e) => setFilter({ ...filter, firm: e.target.value, dept: "" })}
            disabled={!canSelectFirm}
            size="small"
            fullWidth
          >
            {canSelectFirm && <MenuItem value="">Tümü</MenuItem>}
            {effectiveFirms.map((f) => (
              <MenuItem key={f.idfirm} value={f.idfirm}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Departman"
            value={filter.dept}
            onChange={(e) => setFilter({ ...filter, dept: e.target.value })}
            size="small"
            fullWidth
          >
            <MenuItem value="">Tümü</MenuItem>
            {deptOptions.map((d) => (
              <MenuItem key={d.iddepartment} value={d.iddepartment}>
                {d.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Durum"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            size="small"
            fullWidth
          >
            <MenuItem value={STATUS.ALL}>Tüm Durumlar</MenuItem>
            <MenuItem value={STATUS.LOW}>Düşük Bakiye</MenuItem>
            <MenuItem value={STATUS.OK}>Yeterli</MenuItem>
          </TextField>
        </FilterGrid>

        <Box sx={{ mt: 2.5 }}>
          <ListGrid
            rows={filtered}
            columns={columns}
            getRowId={(r) => r.idpersonal}
            loading={loading}
            height={520}
            initialSortModel={[{ field: "nameSurname", sort: "asc" }]}
          />
        </Box>
      </Panel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
